import { useCallback, useState } from "react"
import type { SetStateAction } from "react"

import { addDays, differenceInMinutes, setHours, setMinutes, startOfDay } from "date-fns"
import { toast } from "sonner"

import { uiSlotTypeToDbSlotType } from "@/lib/plannedRouteMapping"
import type {
  Location,
  PlannedRoute,
  PlannedStop,
  SlotPnl,
  SlotService,
} from "@/types/collections"

import { MAX_STOP_DURATION_HOURS, type NewStopFormData, type PendingStopData } from "./types"

export function useNewStopDialog({
  routes,
  selectedDate,
  getLocationById,
  editRoutes,
}: {
  routes: PlannedRoute[]
  selectedDate: Date
  getLocationById: (locationId: string) => Location | undefined
  editRoutes: (update: (prevRoutes: PlannedRoute[]) => PlannedRoute[]) => void
}) {
  const [newStopDialogOpen, setNewStopDialogOpen] = useState(false)
  const [pendingStopData, setPendingStopData] = useState<PendingStopData | null>(null)
  const [newStopFormData, setNewStopFormData] = useState<NewStopFormData>({
    locationId: "",
    startTime: "",
    endTime: "",
    slotType: "Shipper Collection",
    slotService: [],
    slotPnl: [],
  })
  const [locationSearchTerm, setLocationSearchTerm] = useState("")

  const openNewStopDialog = useCallback(
    (pending: PendingStopData, form: SetStateAction<NewStopFormData>) => {
      setNewStopFormData(form)
      setPendingStopData(pending)
      setNewStopDialogOpen(true)
    },
    [],
  )

  const handleCreateStop = useCallback(() => {
    if (!pendingStopData) return

    const location = getLocationById(newStopFormData.locationId)
    if (!location) return

    // Build stops on the route's own date; filteredRoutes shifts them to the selected date for display.
    const originalRoute = routes.find((r) => r.id === pendingStopData.routeId)
    if (!originalRoute) return

    const [startHours, startMinutes] = newStopFormData.startTime.split(":").map(Number)
    const [endHours, endMinutes] = newStopFormData.endTime.split(":").map(Number)

    // Keep the drag's day offset (+1 after midnight) relative to the route's base date.
    const routeBaseDate = startOfDay(originalRoute.date)
    const selectedDateStart = startOfDay(selectedDate)
    const pendingStartDate = startOfDay(pendingStopData.startTime)
    const pendingEndDate = startOfDay(pendingStopData.endTime)

    const pendingStartDayOffset = Math.round(
      (pendingStartDate.getTime() - selectedDateStart.getTime()) / (1000 * 60 * 60 * 24),
    )
    const pendingEndDayOffset = Math.round(
      (pendingEndDate.getTime() - selectedDateStart.getTime()) / (1000 * 60 * 60 * 24),
    )

    const arrivalBaseDate = addDays(routeBaseDate, pendingStartDayOffset)
    const departureBaseDate = addDays(routeBaseDate, pendingEndDayOffset)

    const plannedArrival = setMinutes(
      setHours(new Date(arrivalBaseDate), startHours),
      startMinutes,
    )
    let plannedDeparture = setMinutes(setHours(new Date(departureBaseDate), endHours), endMinutes)

    if (plannedDeparture.getTime() <= plannedArrival.getTime()) {
      plannedDeparture = addDays(plannedDeparture, 1)
    }

    const stopDurationMinutes = differenceInMinutes(plannedDeparture, plannedArrival)
    if (stopDurationMinutes > MAX_STOP_DURATION_HOURS * 60) {
      toast.error("Stop duration too long", {
        description: `Stop duration of ${Math.round(stopDurationMinutes / 60)}h exceeds the maximum of ${MAX_STOP_DURATION_HOURS}h. Please check the start and end times.`,
      })
      return
    }

    let stopType: "collection" | "delivery" | "depot" = "collection"
    if (
      newStopFormData.slotType === "Depot Injection" ||
      newStopFormData.slotType === "Direct Injection" ||
      newStopFormData.slotType === "Internal Stop" ||
      newStopFormData.slotType === "Charging Stop"
    ) {
      stopType = "depot"
    } else if (newStopFormData.slotType === "Haulage Delivery") {
      stopType = "delivery"
    }

    // The temp- prefix marks a stop not yet saved.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const dbSlotType = uiSlotTypeToDbSlotType(newStopFormData.slotType)

    const newStop: PlannedStop = {
      id: tempId,
      routeId: pendingStopData.routeId,
      sequence: 0,
      type: stopType,
      locationId: newStopFormData.locationId,
      name: location.site_name,
      address: location.address,
      postcode: location.postcode,
      lat: location.latitude,
      lng: location.longitude,
      plannedArrival,
      plannedDeparture,
      serviceTimeMinutes: differenceInMinutes(plannedDeparture, plannedArrival),
      volumeEstimated: 0,
      loadingUnitsEstimated: 0,
      slotType: dbSlotType,
      slotServices: newStopFormData.slotService as SlotService[],
      slotPnl: newStopFormData.slotPnl as SlotPnl[],
      haulageDeliveryRevenueGbp:
        dbSlotType === "haulage_delivery"
          ? (newStopFormData.haulageDeliveryRevenueGbp ?? undefined)
          : undefined,
      directInjectionCostGbp:
        dbSlotType === "direct_injection"
          ? (newStopFormData.directInjectionCostGbp ?? undefined)
          : undefined,
    }

    editRoutes((prevRoutes) =>
      prevRoutes.map((route) => {
        if (route.id === pendingStopData.routeId) {
          const updatedStops = [...route.stops, newStop]
            .sort((a, b) => a.plannedArrival.getTime() - b.plannedArrival.getTime())
            .map((stop, idx) => ({ ...stop, sequence: idx + 1 }))

          const newStartTime = updatedStops[0]?.plannedArrival || route.plannedStartTime
          const newEndTime =
            updatedStops[updatedStops.length - 1]?.plannedDeparture || route.plannedEndTime

          return {
            ...route,
            stops: updatedStops,
            totalStops: updatedStops.length,
            plannedStartTime: newStartTime,
            plannedEndTime: newEndTime,
            updatedAt: new Date(),
          }
        }
        return route
      }),
    )

    setNewStopDialogOpen(false)
    setPendingStopData(null)
    setLocationSearchTerm("")
    setNewStopFormData({
      locationId: "",
      startTime: "",
      endTime: "",
      slotType: "Shipper Collection",
      slotService: [],
      slotPnl: [],
      haulageDeliveryRevenueGbp: undefined,
      directInjectionCostGbp: undefined,
    })
  }, [pendingStopData, newStopFormData, selectedDate, routes, getLocationById, editRoutes])

  return {
    newStopDialogOpen,
    setNewStopDialogOpen,
    pendingStopData,
    newStopFormData,
    setNewStopFormData,
    locationSearchTerm,
    setLocationSearchTerm,
    openNewStopDialog,
    handleCreateStop,
  }
}
